import { Crud } from "@/api";
import CartBadge from "@/components/CartBadge/CartBadge";
import { $activeAdminCartId } from "@/stores/adminWorkspace";
import { $cart, CartService } from "@/stores/cart";
import { cn } from "@/utils/cn";
import { usePending } from "@/utils/hooks/usePending.js";
import { Cart } from "@/utils/types/types";
import { Button } from "@mantine/core";
import { useStore } from "@nanostores/react";
import { ChartBar, Trash } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const MENU_TRANSITION_MS = 300;

function CartNameCell({
  id,
  name,
  onSaved,
}: {
  id: string;
  name: string | null;
  onSaved: (id: string, name: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name || "");
  const lastSaved = useRef(name || "");

  const save = async () => {
    setEditing(false);
    if (value === lastSaved.current) return;
    try {
      await Crud.carts.update(id, { name: value } as any);
      lastSaved.current = value;
      onSaved(id, value);
    } catch {
      setValue(lastSaved.current);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") {
            setValue(lastSaved.current);
            setEditing(false);
          }
        }}
        onClick={e => e.stopPropagation()}
        className="w-full truncate rounded bg-white px-1 text-sm font-medium text-[#1e293b] outline outline-1 outline-[#90D9D9]"
      />
    );
  }

  return (
    <span
      title="Click to rename"
      onClick={e => {
        e.stopPropagation();
        setEditing(true);
      }}
      className="cursor-text truncate text-sm font-medium hover:underline hover:decoration-dotted"
    >
      {value || <span className="font-normal text-gray-400">Unnamed</span>}
    </span>
  );
}

export default function AdminOptionsMenu({ isAdmin }: { isAdmin: boolean }) {
  const cart = useStore($cart);
  const activeAdminCartId = useStore($activeAdminCartId);
  const navigate = useNavigate();
  const location = useLocation();

  const isShareable = Boolean(activeAdminCartId);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuMounted, setMenuMounted] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);

  const [shareableCarts, setShareableCarts] = useState<Cart[]>([]);
  const [shareableCartsLoading, setShareableCartsLoading] = useState(false);
  const [cartSearch, setCartSearch] = useState("");
  const [newCartName, setNewCartName] = useState("");
  const [invalidCartName, setInvalidCartName] = useState(false);
  const [duplicateCartName, setDuplicateCartName] = useState(false);

  const filteredCarts = shareableCarts.filter(c => {
    const q = cartSearch.toLowerCase();
    return (c as any).name?.toLowerCase().includes(q) || c.id.includes(q);
  });

  useEffect(() => {
    if (menuOpen) {
      setMenuMounted(true);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setMenuVisible(true))
      );
    } else {
      setMenuVisible(false);
      const t = setTimeout(() => setMenuMounted(false), MENU_TRANSITION_MS);
      return () => clearTimeout(t);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    function handler(e: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("pointerdown", handler, { capture: true });
    return () =>
      document.removeEventListener("pointerdown", handler, { capture: true });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen || !isAdmin) return;
    setShareableCartsLoading(true);
    Crud.carts
      .getAll({ isShareable: true } as any)
      .then(async carts => {
        setShareableCarts(carts);
        const activeCartWasDeleted =
          Boolean(activeAdminCartId) &&
          !carts.some(c => c.id === activeAdminCartId);
        if (activeCartWasDeleted) {
          await CartService.clearAdminWorkspace();
        }
      })
      .catch(() => setShareableCarts([]))
      .finally(() => setShareableCartsLoading(false));
  }, [menuOpen, isAdmin, activeAdminCartId]);

  const refreshShareableCarts = async () => {
    try {
      const carts = await Crud.carts.getAll({ isShareable: true } as any);
      setShareableCarts(carts);
      const activeCartWasDeleted =
        Boolean(activeAdminCartId) &&
        !carts.some(c => c.id === activeAdminCartId);
      if (activeCartWasDeleted) {
        await CartService.clearAdminWorkspace();
      }
    } catch {
      // no-op
    }
  };

  const createShareableCart = usePending(async () => {
    if (!newCartName.trim()) {
      setInvalidCartName(true);
      return;
    }

    const isDuplicate = shareableCarts.some(
      c => (c as any).name?.toLowerCase() === newCartName.toLowerCase()
    );
    if (isDuplicate) {
      setDuplicateCartName(true);
      return;
    }

    const name = newCartName.trim();
    setNewCartName("");
    await CartService.createAdminWorkspaceCart(name);
    await refreshShareableCarts();
  });

  const deleteCart = usePending(
    async (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
      e.stopPropagation();
      if (activeAdminCartId === id) await CartService.clearAdminWorkspace();
      try {
        await Crud.carts.delete(id);
      } catch (err: any) {
        if (err?.response?.status !== 404) throw err;
      }
      await refreshShareableCarts();
    }
  );

  if (!isAdmin) return null;

  return (
    <div ref={menuRef} className="relative hidden md:block">
      <button
        onClick={() => setMenuOpen(o => !o)}
        className={cn(
          "text-md flex items-center gap-3 rounded-full border px-4 py-1.5 font-semibold transition-colors",
          isShareable
            ? "border-[#90D9D9] bg-[#E8F7F7] text-[#246B6B]"
            : "border-[#C1332A] bg-[#C1332A18] text-[#C1332A]"
        )}
      >
        Cart
        <div className="text-left">
          <p className="text-md truncate font-semibold leading-tight text-[#191919]">
            {cart.name ||
              (activeAdminCartId ? "Loading…" : "Admin Master Cart")}
          </p>
          <p className="truncate text-xs font-normal leading-tight text-[#191919]">
            {cart?.id}
          </p>
        </div>
      </button>

      {menuMounted && (
        <div
          className="absolute left-[50%] top-full z-[7000] mt-2 w-[340px] rounded-xl border bg-white shadow-lg"
          style={{
            transition: `opacity ${MENU_TRANSITION_MS}ms ease, transform ${MENU_TRANSITION_MS}ms ease`,
            opacity: menuVisible ? 1 : 0,
            transform: menuVisible
              ? "translateX(-50%) translateY(0px)"
              : "translateX(-50%) translateY(8px)",
            pointerEvents: menuVisible ? "auto" : "none",
          }}
        >
          {/* Active cart */}
          <div className="px-4 py-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">
              Active cart
            </p>
            <CartBadge
              isShareable={isShareable}
              cartId={cart?.id}
              cartName={(cart as any)?.name}
            />
            {!isShareable && (
              <p className="mt-2 text-sm leading-tight text-[#C1332A]">
                You are editing your personal admin cart. Either select or
                create a shareable cart to work in isolation.
              </p>
            )}
          </div>

          {/* Shareable carts list */}
          <div className="border-t px-4 py-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">
                Shareable carts
              </p>
              <input
                className="rounded-md border px-2 text-sm leading-none text-[#191919] focus:outline-[#489B9B]"
                type="text"
                placeholder="Search..."
                value={cartSearch}
                onChange={e => setCartSearch(e.target.value)}
              />
            </div>
            {shareableCartsLoading ? (
              <p className="py-1 text-xs text-[#94a3b8]">Loading…</p>
            ) : shareableCarts.length === 0 ? (
              <p className="py-1 text-xs text-[#94a3b8]">None created yet.</p>
            ) : (
              <div className="flex max-h-44 flex-col gap-0.5 overflow-y-auto">
                {filteredCarts.map(c => {
                  const isActive = activeAdminCartId === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={async () =>
                        await CartService.switchAdminWorkspaceCart(c.id)
                      }
                      className={cn(
                        "flex w-full items-center gap-2 rounded-lg px-3 py-[6px] text-left transition-colors hover:bg-gray-50",
                        isActive && "bg-[#E8F7F7]"
                      )}
                    >
                      <span
                        className={cn(
                          "size-2 shrink-0 rounded-full",
                          isActive ? "bg-[#489B9B]" : "bg-gray-300"
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <CartNameCell
                          id={c.id}
                          name={(c as any).name}
                          onSaved={(id, name) => {
                            setShareableCarts(prev =>
                              prev.map(cart =>
                                cart.id === id
                                  ? ({ ...cart, name } as any)
                                  : cart
                              )
                            );
                            // Also update the $cart store if this is the active cart
                            if (id === activeAdminCartId) {
                              $cart.setKey("name" as any, name);
                            }
                          }}
                        />
                        <p className="truncate text-xs text-[#94a3b8]">
                          {c.id}
                        </p>
                      </div>
                      <Button
                        variant="subtle"
                        color="#C1332A"
                        onClick={e => deleteCart(e, c.id)}
                        style={{ paddingInline: 8 }}
                      >
                        <Trash size={16} />
                      </Button>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Create / return */}
          <div className="flex flex-col gap-2 border-t px-4 py-3">
            <div>
              <input
                value={newCartName}
                onChange={e => {
                  setNewCartName(e.target.value);
                  setInvalidCartName(false);
                  setDuplicateCartName(false);
                }}
                onKeyDown={e => e.key === "Enter" && createShareableCart()}
                placeholder="Cart name..."
                className={cn(
                  "w-full rounded-md border border-[#e2e8f0] bg-white px-3 py-1.5 text-sm text-[#1e293b] outline-none placeholder:text-gray-400 focus:border-[#90D9D9]",
                  (invalidCartName || duplicateCartName) &&
                    "border-[#C1332A] placeholder:text-[#C1332A]"
                )}
              />
              {(invalidCartName || duplicateCartName) && (
                <p className="text-xs text-[#C1332A]">
                  {invalidCartName
                    ? "Cart name is required"
                    : "A cart with this name already exists."}
                </p>
              )}
            </div>
            <Button
              fullWidth
              variant="light"
              loading={createShareableCart.isPending}
              leftSection={
                createShareableCart.isPending ? undefined : (
                  <span className="text-base">+</span>
                )
              }
              onClick={() => createShareableCart()}
              style={{ borderRadius: 8 }}
            >
              New Shareable Cart
            </Button>
            {isShareable && (
              <Button
                fullWidth
                variant="subtle"
                color="#C1332A"
                onClick={() => CartService.clearAdminWorkspace()}
                style={{ borderRadius: 8 }}
              >
                Return to Admin Master Cart
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
